// stateMachine.js — KYC case lifecycle state machine
// Enforces valid state transitions and role-based access for case progression

// ─── Case States ────────────────────────────────────────────────────────
/** @enum {string} */
export const CASE_STATES = Object.freeze({
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  INFORMATION_REQUESTED: 'information_requested',
  SCREENING: 'screening',
  RISK_ASSESSMENT: 'risk_assessment',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  CONDITIONALLY_APPROVED: 'conditionally_approved',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  SUSPENDED: 'suspended',
  CLOSED: 'closed',
});

/** Human-readable labels for each state */
export const STATE_LABELS = Object.freeze({
  [CASE_STATES.DRAFT]: 'Draft',
  [CASE_STATES.SUBMITTED]: 'Submitted',
  [CASE_STATES.IN_REVIEW]: 'In Review',
  [CASE_STATES.INFORMATION_REQUESTED]: 'Information Requested',
  [CASE_STATES.SCREENING]: 'Screening',
  [CASE_STATES.RISK_ASSESSMENT]: 'Risk Assessment',
  [CASE_STATES.PENDING_APPROVAL]: 'Pending Approval',
  [CASE_STATES.APPROVED]: 'Approved',
  [CASE_STATES.CONDITIONALLY_APPROVED]: 'Conditionally Approved',
  [CASE_STATES.REJECTED]: 'Rejected',
  [CASE_STATES.EXPIRED]: 'Expired',
  [CASE_STATES.SUSPENDED]: 'Suspended',
  [CASE_STATES.CLOSED]: 'Closed',
});

// ─── Transition Map ─────────────────────────────────────────────────────
// Each key maps to an array of valid target states.
// Entries can be a plain string (any role) or { state, roles } for restricted transitions.

const TRANSITIONS = Object.freeze({
  [CASE_STATES.DRAFT]: [
    CASE_STATES.SUBMITTED,
  ],

  [CASE_STATES.SUBMITTED]: [
    CASE_STATES.IN_REVIEW,
    CASE_STATES.INFORMATION_REQUESTED,
  ],

  [CASE_STATES.IN_REVIEW]: [
    CASE_STATES.INFORMATION_REQUESTED,
    { state: CASE_STATES.SCREENING, roles: ['bank_compliance_maker', 'platform_admin'] },
    { state: CASE_STATES.REJECTED, roles: ['bank_compliance_maker', 'platform_admin'] },
  ],

  [CASE_STATES.INFORMATION_REQUESTED]: [
    CASE_STATES.SUBMITTED,
    { state: CASE_STATES.IN_REVIEW, roles: ['bank_compliance_maker', 'platform_admin'] },
    CASE_STATES.EXPIRED,
  ],

  [CASE_STATES.SCREENING]: [
    { state: CASE_STATES.RISK_ASSESSMENT, roles: ['bank_compliance_maker', 'platform_admin'] },
    CASE_STATES.INFORMATION_REQUESTED,
    { state: CASE_STATES.REJECTED, roles: ['bank_compliance_maker', 'platform_admin'] },
  ],

  [CASE_STATES.RISK_ASSESSMENT]: [
    { state: CASE_STATES.PENDING_APPROVAL, roles: ['bank_compliance_maker', 'platform_admin'] },
    CASE_STATES.INFORMATION_REQUESTED,
    { state: CASE_STATES.REJECTED, roles: ['bank_compliance_maker', 'platform_admin'] },
  ],

  [CASE_STATES.PENDING_APPROVAL]: [
    { state: CASE_STATES.APPROVED, roles: ['bank_compliance_checker', 'platform_admin'] },
    { state: CASE_STATES.CONDITIONALLY_APPROVED, roles: ['bank_compliance_checker', 'platform_admin'] },
    { state: CASE_STATES.REJECTED, roles: ['bank_compliance_checker', 'platform_admin'] },
    CASE_STATES.INFORMATION_REQUESTED,
  ],

  [CASE_STATES.APPROVED]: [
    CASE_STATES.SUSPENDED,
    CASE_STATES.EXPIRED,
    CASE_STATES.CLOSED,
  ],

  [CASE_STATES.CONDITIONALLY_APPROVED]: [
    { state: CASE_STATES.APPROVED, roles: ['bank_compliance_checker', 'platform_admin'] },
    CASE_STATES.INFORMATION_REQUESTED,
    CASE_STATES.SUSPENDED,
    { state: CASE_STATES.REJECTED, roles: ['bank_compliance_checker', 'platform_admin'] },
    CASE_STATES.EXPIRED,
    CASE_STATES.CLOSED,
  ],

  [CASE_STATES.REJECTED]: [
    CASE_STATES.DRAFT,
    CASE_STATES.CLOSED,
  ],

  [CASE_STATES.EXPIRED]: [
    CASE_STATES.DRAFT,
    CASE_STATES.CLOSED,
  ],

  [CASE_STATES.SUSPENDED]: [
    { state: CASE_STATES.IN_REVIEW, roles: ['bank_compliance_maker', 'platform_admin'] },
    CASE_STATES.CLOSED,
    { state: CASE_STATES.APPROVED, roles: ['bank_compliance_checker', 'platform_admin'] },
  ],

  [CASE_STATES.CLOSED]: [
    // Terminal state — no outgoing transitions
  ],
});

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Normalise a transition entry into { state, roles }.
 * @param {string | { state: string, roles: string[] }} entry
 * @returns {{ state: string, roles: string[] | null }}
 */
function normaliseEntry(entry) {
  if (typeof entry === 'string') return { state: entry, roles: null };
  return { state: entry.state, roles: entry.roles ?? null };
}

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * Can the case move from `currentState` to `targetState` (ignoring roles)?
 * @param {string} currentState
 * @param {string} targetState
 * @returns {boolean}
 */
export function canTransition(currentState, targetState) {
  const entries = TRANSITIONS[currentState];
  if (!entries) return false;
  return entries.some((e) => {
    const { state } = normaliseEntry(e);
    return state === targetState;
  });
}

/**
 * Return the states the case can move to from `currentState`,
 * filtered by the caller's role when transitions are role-restricted.
 * @param {string} currentState
 * @param {string} [userRole]
 * @returns {string[]}
 */
export function getAvailableTransitions(currentState, userRole) {
  const entries = TRANSITIONS[currentState];
  if (!entries) return [];

  return entries
    .map(normaliseEntry)
    .filter(({ roles }) => {
      if (!roles) return true; // no role restriction
      if (!userRole) return false; // restricted but no role supplied
      return roles.includes(userRole);
    })
    .map(({ state }) => state);
}

/**
 * Validate whether a transition is allowed and explain why not if invalid.
 * @param {string} currentState
 * @param {string} targetState
 * @param {string} [userRole]
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateTransition(currentState, targetState, userRole) {
  // Guard: unknown current state
  if (!TRANSITIONS[currentState]) {
    return { valid: false, reason: `Unknown current state: "${currentState}"` };
  }

  // Guard: unknown target state
  const allStates = Object.values(CASE_STATES);
  if (!allStates.includes(targetState)) {
    return { valid: false, reason: `Unknown target state: "${targetState}"` };
  }

  const entries = TRANSITIONS[currentState];
  const match = entries
    .map(normaliseEntry)
    .find(({ state }) => state === targetState);

  if (!match) {
    return {
      valid: false,
      reason: `Transition from "${currentState}" to "${targetState}" is not allowed`,
    };
  }

  if (match.roles) {
    if (!userRole) {
      return {
        valid: false,
        reason: `Transition to "${targetState}" requires one of these roles: ${match.roles.join(', ')}`,
      };
    }
    if (!match.roles.includes(userRole)) {
      return {
        valid: false,
        reason: `Role "${userRole}" cannot transition to "${targetState}". Required: ${match.roles.join(', ')}`,
      };
    }
  }

  return { valid: true };
}
